import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useProject } from "@/lib/workflow/useProject";
import { GUEST_STATUSES } from "@/lib/workflow/types";

const field = "w-full rounded-md border bg-background px-3 py-2 text-sm";
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * Event Suite Premium: email invitations and reminders to the guests in the open project. The message is the
 * person's own; it goes out under their name, replies come to their own address, and it can carry attachments.
 */
export function EmailGuests() {
  const [project] = useProject();
  const utils = trpc.useUtils();
  const status = trpc.eventSuite.mail.status.useQuery(undefined, { retry: false });
  const send = trpc.eventSuite.mail.send.useMutation();
  const cancel = trpc.eventSuite.mail.cancel.useMutation();
  const prepare = trpc.eventSuite.mail.prepareAttachments.useMutation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [replyTo, setReplyTo] = useState("");
  const [busy, setBusy] = useState(false);

  const [audience, setAudience] = useState<string>("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("Dear {name},\n\nYou are invited to our event. Please reply to this email to let us know if you can come.\n\nWe hope to see you there.");
  const [later, setLater] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [permission, setPermission] = useState(false);
  const [rsvp, setRsvp] = useState(true);
  const responses = trpc.eventSuite.text.responses.useQuery({ eventName: project.name }, { retry: false });

  const withEmail = useMemo(() => project.data.guests.filter((g) => !!g.email?.trim()), [project.data.guests]);
  const emailChoosers = useMemo(() => new Set((responses.data ?? []).filter((r) => r.status === "accepted" && r.channel === "email").map((r) => r.email.toLowerCase())), [responses.data]);
  const chosen = useMemo(() => {
    if (audience === "all") return withEmail;
    if (audience === "rsvp-email") return withEmail.filter((g) => emailChoosers.has((g.email ?? "").trim().toLowerCase()));
    return withEmail.filter((g) => g.status === audience);
  }, [withEmail, audience, emailChoosers]);
  const left = status.data ? status.data.dailyLimit - status.data.sentLast24h : null;

  if (status.isError) return null;

  const maxFiles = status.data?.maxAttachments ?? 3;
  const maxBytes = status.data?.maxAttachmentBytes ?? 10_000_000;
  const totalBytes = files.reduce((n, f) => n + f.size, 0);
  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...files, ...Array.from(list)];
    if (next.length > maxFiles) toast.error(`Attach at most ${maxFiles} files.`);
    else if (next.reduce((n, f) => n + f.size, 0) > maxBytes) toast.error(`Attachments can total at most ${Math.round(maxBytes / 1_000_000)} MB.`);
    else setFiles(next);
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async () => {
    setBusy(true);
    try {
      let mailId: string | undefined;
      if (files.length) {
        const prep = await prepare.mutateAsync({ files: files.map((f) => ({ name: f.name, sizeBytes: f.size })) });
        mailId = prep.mailId;
        await Promise.all(prep.uploads.map(async (u, i) => {
          const res = await fetch(u.url, { method: "PUT", headers: { "Content-Type": "application/octet-stream" }, body: files[i] });
          if (!res.ok) throw new Error(`Could not upload ${files[i].name}. Please try again.`);
        }));
      }
      const r = await send.mutateAsync({
        mailId, attachments: files.length ? files.map((f) => ({ name: f.name, sizeBytes: f.size })) : undefined,
        replyTo: replyTo.trim() || undefined,
        rsvp,
        subject, body, eventName: project.name,
        recipients: chosen.map((g) => ({ name: g.name, email: g.email! })),
        sendAt: later && sendAt ? new Date(sendAt).toISOString() : null,
        confirmedPermission: true,
      });
      const skipped = r.skipped.invalid + r.skipped.duplicates + r.skipped.optedOut;
      const note = skipped ? ` ${skipped} skipped (invalid, repeated or unsubscribed).` : "";
      if (r.queued) toast.success(`Sending to ${r.count} guest${r.count === 1 ? "" : "s"} now. With attachments this takes a minute or two.${note}`);
      else if (r.scheduled) toast.success(`Scheduled for ${r.count} guest${r.count === 1 ? "" : "s"}.${note}`);
      else if (r.failed) toast.error(`${r.sent} sent, ${r.failed} could not be sent.${note}`);
      else toast.success(`Sent to ${r.sent} guest${r.sent === 1 ? "" : "s"}.${note}`);
      setPermission(false);
      setFiles([]);
      await utils.eventSuite.mail.status.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const canSend = chosen.length > 0 && subject.trim().length >= 3 && body.trim().length >= 10 && permission && (!later || !!sendAt) && !busy;

  return (
    <section className="mt-3 rounded-lg border bg-card p-4 text-sm" data-testid="email-guests">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 font-semibold"><Mail className="h-4 w-4" aria-hidden />Email your guests</h3>
        {left !== null && <span className="text-xs text-muted-foreground">{left} of {status.data!.dailyLimit} emails left in the last 24 hours</span>}
      </div>

      <label className="mt-3 block">
        <span className="text-xs text-muted-foreground">Guests' replies come to</span>
        <input className={field} type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder={status.data?.replyTo || "your email"} data-testid="email-reply-to" />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-muted-foreground">Send to</span>
          <select className={field} value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="email-audience">
            <option value="all">All guests with an email ({withEmail.length})</option>
            <option value="rsvp-email">Chose email reminders ({withEmail.filter((g) => emailChoosers.has((g.email ?? "").trim().toLowerCase())).length})</option>
            {GUEST_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)} ({withEmail.filter((g) => g.status === s).length})</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Subject</span>
          <input className={field} value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} placeholder="You are invited" data-testid="email-subject" />
        </label>
      </div>
      <label className="mt-3 block">
        <span className="text-xs text-muted-foreground">Message. {"{name}"} becomes each guest's first name.</span>
        <textarea className={`${field} min-h-[140px]`} value={body} maxLength={5000} onChange={(e) => setBody(e.target.value)} data-testid="email-body" />
      </label>

      <label className="mt-3 flex items-start gap-2 text-xs">
        <input type="checkbox" className="mt-0.5" checked={rsvp} onChange={(e) => setRsvp(e.target.checked)} data-testid="email-rsvp" />
        <span>Add a personal RSVP link for each guest. They can accept or decline and choose email or text reminders. Put {"{rsvp}"} in the message where the link should go, or it is added at the end.</span>
      </label>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="email-files" />
        <Button size="sm" variant="outline" type="button" onClick={() => fileInput.current?.click()} disabled={files.length >= maxFiles}><Paperclip className="h-3.5 w-3.5 mr-1" />Attach files</Button>
        <span className="text-xs text-muted-foreground">Up to {maxFiles} files, {Math.round(maxBytes / 1_000_000)} MB in total. Every guest gets the same files.</span>
      </div>
      {files.length > 0 && (
        <ul className="mt-2 text-xs" data-testid="email-file-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 py-0.5">
              <Paperclip className="h-3 w-3 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="text-muted-foreground">{(f.size / 1_000_000).toFixed(2)} MB</span>
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
            </li>
          ))}
          <li className="text-muted-foreground">Total {(totalBytes / 1_000_000).toFixed(2)} MB</li>
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2"><input type="radio" checked={!later} onChange={() => setLater(false)} />Send now</label>
        <label className="flex items-center gap-2"><input type="radio" checked={later} onChange={() => setLater(true)} />Send later (a reminder)</label>
        {later && <input type="datetime-local" className="rounded-md border bg-background px-2 py-1" value={sendAt} onChange={(e) => setSendAt(e.target.value)} data-testid="email-send-at" />}
      </div>

      <label className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
        <input type="checkbox" className="mt-0.5" checked={permission} onChange={(e) => setPermission(e.target.checked)} data-testid="email-permission" />
        <span>I have permission to email these guests about this event. Replies come to the email address above, and every message has an unsubscribe link.</span>
      </label>

      <div className="mt-3 flex items-center gap-3">
        <Button size="sm" onClick={() => void submit()} disabled={!canSend} data-testid="email-send">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
          {later ? "Schedule" : "Send"} to {chosen.length} guest{chosen.length === 1 ? "" : "s"}
        </Button>
        {withEmail.length === 0 && <span className="text-xs text-muted-foreground">Add guests with email addresses in Guest Manager first.</span>}
      </div>

      {status.data && status.data.scheduled.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Scheduled</h4>
          <ul className="mt-1 divide-y">
            {status.data.scheduled.map((s) => (
              <li key={s.id} className="flex items-center gap-2 py-1.5">
                <span className="min-w-0 flex-1 truncate"><strong>{s.subject}</strong><span className="ml-2 text-xs text-muted-foreground">{s.recipientCount} guests, {when(s.sendAt)}</span></span>
                <Button size="sm" variant="ghost" aria-label={`Cancel ${s.subject}`} onClick={async () => { await cancel.mutateAsync({ id: s.id }); await utils.eventSuite.mail.status.invalidate(); }}><X className="h-3.5 w-3.5" /></Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {status.data && status.data.recent.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recently sent</h4>
          <ul className="mt-1 text-xs text-muted-foreground">
            {status.data.recent.map((r) => <li key={r.id}>{r.subject}: {r.status === "sent" ? `${r.sentCount} sent` : "could not be sent"}, {when(r.at)}</li>)}
          </ul>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">We keep the guest list and any attachments only until the message has been sent. Reminders are sent within a minute of the time you choose.</p>
    </section>
  );
}
